import { caseFieldDisplayValue } from "@/features/catalog_case_review/shared/utils/caseFieldDisplayValue";
import { parseProposedValue } from "@/features/catalog_case_review/shared/utils/caseFieldValueParser";
import type { ProposalAlternative, ProposalForm } from "@/features/catalog_case_review/shared/model/agentProposal";
import type { CaseFieldValue } from "@/features/catalog_case_review/shared/model/caseReview";
import { LOCK_STATES, PAGE_KINDS, REVIEW_MODES } from "../shared/constants/pageAgentIds";
import type { AgentFieldInput } from "../shared/model/caseReviewAgentBindings";
import type { PageLocation } from "../shared/model/pageLocation";
import { FRONTEND_TOOL_CODES, FRONTEND_TOOL_NAMES } from "./frontendToolNames";
import { constStringNode, enumStringNode, isRefusal, optionalString, refuse, requiredString, type FrontendTool, type FrontendToolArgs, type FrontendToolEnvironment, type FrontendToolResult } from "./frontendTool";

/** One field of a successful proposal, as the control shows it before and after. */
export interface ProposedChange {
  fieldId: string;
  fieldLabel: string;
  previousValue: string;
  value: string;
}

/** What a successful proposal answers; the dock's card is drawn from exactly this. */
export type ProposeFieldValueResult = {
  ok: true;
  key: string;
  recordId: string | null;
  changes: ProposedChange[];
  rationale: string;
};

/** Whether this is the answer of a successful `proposeFieldValue`. */
export function isProposal(result: FrontendToolResult): result is ProposeFieldValueResult {
  return result.ok && typeof result.key === "string" && Array.isArray(result.changes);
}

const ARG_FIELD_ID = "fieldId";
const ARG_VALUE = "value";
const ARG_VALUES = "values";
const ARG_ALTERNATIVES = "alternatives";
const ARG_LABEL = "label";
/**
 * Every value travels as text: the ADK LangChain4j bridge keeps only `type`, `description`,
 * `properties` and `items` of a declaration and rejects a node without a type, so the schema
 * cannot say "any JSON". The executor parses the text per field kind (option label, date,
 * yes/no, number, JSON for criteria); `null`, or the word null, clears the field.
 */
const VALUE_DESCRIPTION = "The proposed value as text: the active option label for a coded field, YYYY-MM-DD for a date, yes or no for a yes/no field, digits for a number, a JSON object of true/false flags for criteria, keyed by the criterion names on screen (criteria you leave out keep their value); the word null clears the field.";

/** A parsed field with the control it was parsed by, so the answer can show old and new the way the control does. */
interface ParsedField {
  fieldId: string;
  input: AgentFieldInput;
  value: CaseFieldValue;
}

/**
 * Puts a value into one editable field of the section on screen — or several at once, set and
 * reverted together — as a proposal the reviewer must accept or reject.
 *
 * <p>Declared only in edit mode, with the editable fields as the `fieldId` enum and the keys of
 * `values`, and the selected record as a constant, so the model cannot name anything else. The
 * executor still re-checks every one of those against the screen as it is now, and validates each
 * value the way the control would: scope is enforced here and by the reviewer's decision, never by
 * the model's good behaviour. Nothing is applied unless every key passes.
 */
export const proposeFieldValueTool: FrontendTool = {
  name: FRONTEND_TOOL_NAMES.proposeFieldValue,
  declaration: (location, caseReview) => {
    const section = location?.section;
    if (location?.page !== PAGE_KINDS.caseReview || caseReview === null || location.mode !== REVIEW_MODES.edit
      || !section || section.editableFieldIds.length === 0) return null;
    const valuesByField = {
      type: "object",
      properties: Object.fromEntries(section.editableFieldIds.map((fieldId) => [fieldId, { type: "string", description: VALUE_DESCRIPTION }])),
      additionalProperties: false,
    };
    return {
      name: FRONTEND_TOOL_NAMES.proposeFieldValue,
      description: "Propose a value for one editable field of the section on screen, or several fields together with `values` (a MedDRA term and its preferred term; a causality method and its result). The reviewer accepts or rejects the proposal as one; nothing is saved by this call. Use the field's active option label for coded fields, YYYY-MM-DD for dates, and give the reason with the page it came from. Other candidates you considered go in `alternatives`, each with a label and its values, so the reviewer can step to them.",
      parameters: {
        type: "object",
        properties: {
          [ARG_FIELD_ID]: enumStringNode(section.editableFieldIds),
          ...(section.recordId === null ? {} : { recordId: constStringNode(section.recordId) }),
          [ARG_VALUE]: { type: "string", description: VALUE_DESCRIPTION },
          [ARG_VALUES]: { ...valuesByField, description: "Several fields to set together, by field id; give either this or fieldId with value." },
          [ARG_ALTERNATIVES]: {
            type: "array",
            description: "Other candidates, best first, each with a short label and the same field keys as the proposal.",
            items: { type: "object", properties: { [ARG_LABEL]: { type: "string" }, [ARG_VALUES]: valuesByField }, required: [ARG_LABEL, ARG_VALUES] },
          },
          rationale: { type: "string", description: "Why, with the source page." },
        },
        required: ["rationale", ...(section.recordId === null ? [] : ["recordId"])],
      },
    };
  },
  execute: (args, env) => {
    const rationale = requiredString(args, "rationale");
    if (isRefusal(rationale)) return rationale;
    const recordId = optionalString(args, "recordId");
    if (isRefusal(recordId)) return recordId;
    const requested = requestedValues(args);
    if (isRefusal(requested)) return requested;
    const pair = pairHonoured(env, Object.keys(requested));
    if (isRefusal(pair)) return pair;
    const scope = inScope(env, Object.keys(requested), recordId ?? null);
    if (isRefusal(scope)) return scope;
    const fields = parseFields(env, requested, scope);
    if (isRefusal(fields)) return fields;
    const named = parseAlternatives(env, args, scope, Object.keys(requested));
    if (isRefusal(named)) return named;
    // Every bad value at once, the proposal's and the alternatives', so a single retry can fix them all.
    const failures = [...fields.failures, ...named.failures];
    if (failures.length > 0) return refuse(FRONTEND_TOOL_CODES.invalidValue, failures.join(" "));
    const { parsed } = fields;
    const { alternatives } = named;
    const bindings = env.caseReview!;
    const outcome = bindings.proposals.propose({
      form: scope, values: Object.fromEntries(parsed.map((field) => [field.fieldId, field.value])), rationale,
      runId: env.call.runId, toolCallId: env.call.toolCallId, ...(alternatives.length === 0 ? {} : { alternatives }),
    });
    if (!outcome.ok) return refuse(FRONTEND_TOOL_CODES.outOfScope, outcome.message);
    const result: ProposeFieldValueResult = {
      ok: true, key: outcome.proposal.key, recordId: scope.recordId, rationale,
      changes: parsed.map((field) => ({
        fieldId: field.fieldId, fieldLabel: field.input.label,
        previousValue: caseFieldDisplayValue(field.input.value, field.input.kind, field.input.options),
        value: caseFieldDisplayValue(field.value, field.input.kind, field.input.options),
      })),
    };
    return result;
  },
};

/**
 * A turn started from a paired field (a MedDRA PT with its LLT, a causality method with its result)
 * sets both or nothing: the catalog declares the pair, the button pins it, and this is where the
 * browser holds the model to it. A proposal the reviewer asked for in chat carries no pin and is free.
 */
function pairHonoured(env: FrontendToolEnvironment, fieldIds: string[]): true | FrontendToolResult {
  const action = env.call.fieldAction;
  if (!action || action.pairFieldId === undefined) return true;
  const expected = [action.fieldId, action.pairFieldId];
  const complete = fieldIds.length === expected.length && expected.every((fieldId) => fieldIds.includes(fieldId));
  if (complete) return true;
  return refuse(FRONTEND_TOOL_CODES.invalidValue, `This action sets ${action.fieldId} and ${action.pairFieldId} together; give both in ${ARG_VALUES}.`);
}

/** The fields asked for, by id: `values`, or the single `fieldId` / `value` pair. Exactly one form of the two. */
function requestedValues(args: FrontendToolArgs): Record<string, unknown> | FrontendToolResult {
  const values = args[ARG_VALUES];
  const single = ARG_FIELD_ID in args || ARG_VALUE in args;
  if (values !== undefined && single) return refuse(FRONTEND_TOOL_CODES.invalidValue, `Give either ${ARG_VALUES} or ${ARG_FIELD_ID} with ${ARG_VALUE}, not both.`);
  if (values !== undefined) {
    if (typeof values !== "object" || values === null || Array.isArray(values)) return refuse(FRONTEND_TOOL_CODES.invalidValue, `${ARG_VALUES} must be an object keyed by field id.`);
    if (Object.keys(values).length === 0) return refuse(FRONTEND_TOOL_CODES.invalidValue, `${ARG_VALUES} names no field.`);
    return values as Record<string, unknown>;
  }
  const fieldId = requiredString(args, ARG_FIELD_ID);
  if (isRefusal(fieldId)) return fieldId;
  if (!(ARG_VALUE in args)) return refuse(FRONTEND_TOOL_CODES.invalidValue, `${ARG_VALUE} is required (the word null clears the field).`);
  return { [fieldId]: args[ARG_VALUE] };
}

/**
 * Every requested field parsed by its control: the fields that parsed and a message per field that
 * did not (with its options, for a coded field). A field off the screen refuses at once.
 */
function parseFields(env: FrontendToolEnvironment, values: Record<string, unknown>, scope: ProposalForm): { parsed: ParsedField[]; failures: string[] } | FrontendToolResult {
  const parsed: ParsedField[] = [];
  const failures: string[] = [];
  for (const [fieldId, raw] of Object.entries(values)) {
    const input = env.caseReview!.fieldInput(fieldId, scope.recordId);
    if (input === undefined) return refuse(FRONTEND_TOOL_CODES.outOfScope, `${fieldId} is not a field of the section on screen.`);
    const value = parseProposedValue(input.kind, input.options, raw, input.value);
    if (value.ok) parsed.push({ fieldId, input, value: value.value });
    else failures.push(`${fieldId}: ${value.message}`);
  }
  return { parsed, failures };
}

/** The named alternatives, each parsed like the proposal and confined to the same fields, with a message per bad value. */
function parseAlternatives(env: FrontendToolEnvironment, args: FrontendToolArgs, scope: ProposalForm,
                           fieldIds: string[]): { alternatives: ProposalAlternative[]; failures: string[] } | FrontendToolResult {
  const raw = args[ARG_ALTERNATIVES];
  if (raw === undefined || raw === null) return { alternatives: [], failures: [] };
  if (!Array.isArray(raw)) return refuse(FRONTEND_TOOL_CODES.invalidValue, `${ARG_ALTERNATIVES} must be a list.`);
  const alternatives: ProposalAlternative[] = [];
  const failures: string[] = [];
  for (const candidate of raw as Array<Record<string, unknown>>) {
    const label = requiredString(candidate, ARG_LABEL);
    if (isRefusal(label)) return label;
    const values = candidate[ARG_VALUES];
    if (typeof values !== "object" || values === null || Array.isArray(values)) return refuse(FRONTEND_TOOL_CODES.invalidValue, `An alternative's ${ARG_VALUES} must be an object keyed by field id.`);
    const keys = Object.keys(values);
    if (keys.length !== fieldIds.length || !keys.every((key) => fieldIds.includes(key))) {
      return refuse(FRONTEND_TOOL_CODES.invalidValue, `Alternative "${label}" must set exactly the proposal's fields: ${fieldIds.join(", ")}.`);
    }
    const fields = parseFields(env, values as Record<string, unknown>, scope);
    if (isRefusal(fields)) return fields;
    failures.push(...fields.failures.map((failure) => `Alternative "${label}", ${failure}`));
    alternatives.push({ label, values: Object.fromEntries(fields.parsed.map((field) => [field.fieldId, field.value])) });
  }
  return { alternatives, failures };
}

/** The live checks the declaration's enum already implied, against the screen as it is now, for every field named. */
function inScope(env: FrontendToolEnvironment, fieldIds: string[], recordId: string | null): ProposalForm | FrontendToolResult {
  const location: PageLocation | null = env.location;
  const section = location?.section;
  if (location?.page !== PAGE_KINDS.caseReview || !section || env.caseReview === null) {
    return refuse(FRONTEND_TOOL_CODES.outOfScope, "No case section is open on this screen.");
  }
  if (location.mode !== REVIEW_MODES.edit) return refuse(FRONTEND_TOOL_CODES.outOfScope, "The case is in read mode. Ask the reviewer to press Edit case first.");
  if (location.lock !== LOCK_STATES.held) return refuse(FRONTEND_TOOL_CODES.outOfScope, "This browser does not hold the case's edit lock.");
  if (env.caseReview.saveInFlight) return refuse(FRONTEND_TOOL_CODES.outOfScope, "A save is in progress; try again once it has finished.");
  for (const fieldId of fieldIds) {
    if (!fieldId.startsWith(`${section.id}.`)) return refuse(FRONTEND_TOOL_CODES.outOfScope, `${fieldId} is not in the section on screen (${section.id}). Use goToField first.`);
  }
  if (recordId !== section.recordId && !(recordId === null && section.recordId === null)) {
    return refuse(FRONTEND_TOOL_CODES.outOfScope, section.recordId === null
      ? "The section on screen has no selected record." : `Only the selected record (${section.recordId}) can be proposed to.`);
  }
  for (const fieldId of fieldIds) {
    if (!section.editableFieldIds.includes(fieldId)) return refuse(FRONTEND_TOOL_CODES.outOfScope, `${fieldId} is not editable here.`);
  }
  return { sectionId: section.id, collectionId: section.collectionId, recordId: section.recordId };
}
